import React, { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import {
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Send as SendIcon } from "@mui/icons-material";
import { format, isToday, isYesterday, parseISO } from "date-fns";
import { createOrderNote, getOrderNotes } from "src/queries/query";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// Helper function to format date for display
const formatMessageDate = (dateString) => {
  const date = parseISO(dateString);

  if (isToday(date)) {
    return "Today";
  } else if (isYesterday(date)) {
    return "Yesterday";
  } else {
    return format(date, "MMM dd, yyyy");
  }
};

// Helper function to format time for display
const formatMessageTime = (dateString) => {
  const date = parseISO(dateString);
  return format(date, "HH:mm");
};

export default function Notes({
  shipperNotes,
  consigneeNotes,
  orderId,
  manifestId,
  showInput = true,
  isManifest = false,
}) {
  const [inputText, setInputText] = useState("");
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const queryClient = useQueryClient();

  const formik = useFormik({
    initialValues: {
      internalNote: "",
    },
    onSubmit: (values) => {
      formik.resetForm();
    },
  });

  // Fetch order notes
  const {
    data: orderNotesData,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ["order-notes", orderId, manifestId],
    queryFn: () =>
      getOrderNotes(
        isManifest ? null : orderId,
        isManifest ? manifestId : null,
      ),
    enabled: !!(isManifest ? manifestId : orderId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const orderNotes = orderNotesData?.data || [];

  // Create note mutation
  const createNoteMutation = useMutation({
    mutationFn: (payload) => createOrderNote(payload),
    onSuccess: (response) => {
      // Invalidate and refetch the order notes query
      queryClient.invalidateQueries({
        queryKey: ["order-notes", orderId, manifestId],
      });
      setInputText("");
      setError(null);
      setTimeout(scrollToBottom, 100);
    },
    onError: (err) => {
      setError(err.response?.data?.message || "Failed to create note");
      console.error("Error creating note:", err);
    },
  });

  // Handle query errors
  useEffect(() => {
    if (queryError) {
      setError(
        queryError.response?.data?.message || "Failed to load order notes",
      );
    }
  }, [queryError]);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  // Scroll to bottom when notes change
  useEffect(() => {
    scrollToBottom();
  }, [orderNotes]);

  // Handle send note
  const handleSendNote = () => {
    if (!inputText.trim() || createNoteMutation.isPending) return;

    const noteText = inputText.trim();
    setError(null);

    const payload = {
      note: noteText,
    };

    if (isManifest) {
      payload.manifestId = manifestId;
      payload.orderId = null;
    } else {
      payload.orderId = orderId;
      payload.manifestId = null;
    }

    console.log("Notes - Sending note payload:", payload);

    createNoteMutation.mutate(payload);
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendNote();
    }
  };
  const groupedNotes = orderNotes.reduce((acc, note) => {
    const dateKey = formatMessageDate(note.createdAt);
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(note);
    return acc;
  }, {});

  // Sort notes within each date (ascending - oldest first)
  Object.keys(groupedNotes).forEach((dateKey) => {
    groupedNotes[dateKey].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    );
  });

  const sortedDateKeys = Object.keys(groupedNotes).sort((a, b) => {
    if (a === "Today") return 1;
    if (b === "Today") return -1;
    if (a === "Yesterday") return 1;
    if (b === "Yesterday") return -1;
    return (
      parseISO(groupedNotes[a][0].createdAt) -
      parseISO(groupedNotes[b][0].createdAt)
    );
  });

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Scrollable notes section */}
      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          p: { xs: 1.5, sm: 2 },
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#c1c1c1",
            borderRadius: "3px",
            "&:hover": {
              background: "#a8a8a8",
            },
          },
        }}
      >
        {/* Legacy Shipper/Consignee Notes */}
        {!isManifest ? (
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Shipper Notes
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {shipperNotes || "No shipper notes available"}
            </Typography>
            <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
              Consignee Notes
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {consigneeNotes || "No consignee notes available"}
            </Typography>
          </Box>
        ) : null}

        {/* Error Message */}
        {error && (
          <Box
            sx={{
              p: 1.5,
              mb: 2,
              backgroundColor: "#fee",
              border: "1px solid #fcc",
              borderRadius: 1,
              borderLeft: "3px solid #f44",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#c33", fontSize: "0.75rem" }}
            >
              {error}
            </Typography>
          </Box>
        )}

        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              gap: 2,
            }}
          >
            <CircularProgress size={32} sx={{ color: "#1e55bf" }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Loading notes...
            </Typography>
          </Box>
        ) : sortedDateKeys.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                textAlign: "center",
                fontWeight: 500,
              }}
            >
              No {isManifest ? "manifest" : "order"} notes yet
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", textAlign: "center" }}
            >
              Create a note using the input below
            </Typography>
          </Box>
        ) : (
          sortedDateKeys.map((dateKey) => (
            <Box key={dateKey} sx={{ mb: 2 }}>
              {/* Date Header */}
              <Box
                sx={{
                  textAlign: "center",
                  mb: 1,
                  position: "sticky",
                  top: -20,
                  zIndex: 1,
                  backgroundColor: "white",
                  py: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "#1e55bf",
                    backgroundColor: "#e3f2fd",
                    borderRadius: 1,
                    px: 2,
                    py: 0.75,
                    display: "inline-block",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    border: "1px solid #bbdefb",
                  }}
                >
                  {dateKey}
                </Typography>
              </Box>

              {/* Notes for this date */}
              {groupedNotes[dateKey].map((note) => {
                const fullName = note.user
                  ? `${note.user.firstName} ${note.user.lastName}`
                  : note.createdBy || "System";
                return (
                  <Box
                    key={note.id}
                    sx={{
                      display: "flex",
                      mb: 2.5,
                      alignItems: "flex-start",
                      gap: 1.5,
                      p: { xs: 1, sm: 1.5 },
                      borderRadius: 2,
                      backgroundColor: "#fff3e0",
                      transition: "background-color 0.2s",
                      "&:hover": {
                        backgroundColor: "#ffe0b2",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "4px",
                        height: "4px",
                        borderRadius: "50%",
                        backgroundColor: "#1e55bf",
                        mt: 1,
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: { xs: "0.8rem", sm: "0.875rem" },
                          lineHeight: 1.6,
                          mb: 0.5,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            fontWeight: 600,
                            color: "text.primary",
                            mr: 1,
                          }}
                        >
                          {fullName}
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          {note.note}
                        </Box>
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Box component="span">
                          {formatMessageTime(note.createdAt)}
                        </Box>
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Fixed bottom input section */}
      {showInput && (
        <Box
          sx={{
            p: 2,
            backgroundColor: "#eeeeee99",
            borderRadius: 0,
            flexShrink: 0,
          }}
        >
          <Typography
            fontWeight="bold"
            color="primary"
            sx={{ display: "flex", alignItems: "center", mb: 1 }}
          >
            🗒️ Create {isManifest ? "Manifest" : "Order"} Note
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <TextField
              fullWidth
              multiline
              maxRows={3}
              variant="outlined"
              placeholder="Type a note..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={createNoteMutation.isPending}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  backgroundColor: "#f8f9fa",
                  fontSize: "0.875rem",
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#bdbdbd",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1e55bf",
                    borderWidth: "1.5px",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#f5f5f5",
                  },
                },
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSendNote}
              disabled={!inputText.trim() || createNoteMutation.isPending}
              sx={{
                backgroundColor: "#1e55bf",
                color: "white",
                width: 40,
                height: 40,
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: "#1648a3",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#e0e0e0",
                  color: "#9e9e9e",
                },
              }}
            >
              {createNoteMutation.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SendIcon sx={{ fontSize: "1.1rem" }} />
              )}
            </IconButton>
          </Box>

          {/* Legacy form (hidden but kept for compatibility) */}
          <form onSubmit={formik.handleSubmit} className="hidden">
            <TextField
              fullWidth
              multiline
              minRows={3}
              name="internalNote"
              placeholder="Write your note here..."
              variant="outlined"
              value={formik.values.internalNote}
              onChange={formik.handleChange}
            />
          </form>
        </Box>
      )}
    </Box>
  );
}
